import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { Reservation } from '../../types/reservation';
import { Vehicle } from '../../types/vehicle';
import { Fleet } from '../../types';
import { reservationService } from '../../services/reservationService';
import { icsService } from '../../services/icsService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ReservationConfirmationProps {
  reservation: Reservation;
  vehicle: Vehicle;
  fleet: Fleet;
  onNewReservation?: () => void;
}

const ReservationConfirmation: React.FC<ReservationConfirmationProps> = ({
  reservation,
  vehicle,
  fleet,
  onNewReservation
}) => {
  const { t } = useTranslation();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(reservation.status === 'cancelled');
  const [error, setError] = useState<string | null>(null);

  const handleDownloadICS = () => {
    try {
      icsService.generateICSFile(reservation, vehicle, fleet);
    } catch (error) {
      console.error('Error downloading ICS:', error);
      setError(t('reservation:errors.icsDownloadFailed', 'Erreur lors du téléchargement du fichier ICS'));
    }
  };

  const handleCancelReservation = async () => {
    setCancelling(true);
    setError(null);

    try {
      await reservationService.cancelReservationByUser(
        reservation.id,
        reservation.userEmail
      );
      setCancelled(true);
      setCancelDialogOpen(false);
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (timestamp: any) => {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'EEEE dd MMMM yyyy à HH:mm', { locale: fr });
  };

  if (cancelled) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          borderLeft: 4,
          borderColor: 'error.main'
        }}
      >
        <CancelIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {t('reservation:confirmation.cancelled', 'Réservation annulée')}
        </Typography>
        <Typography color="text.secondary" paragraph>
          {t('reservation:confirmation.cancelledMessage', 'Cette réservation a été annulée avec succès.')}
        </Typography>
        {onNewReservation && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onNewReservation}
            sx={{ mt: 2 }}
          >
            {t('reservation:confirmation.newReservation', 'Nouvelle réservation')}
          </Button>
        )}
      </Paper>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          borderLeft: 4,
          borderColor: 'success.main'
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />

        <Typography variant="h5" gutterBottom>
          {t('reservation:confirmation.title', 'Réservation confirmée')}
        </Typography>

        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          {vehicle.make} {vehicle.model} — {t('reservation:confirmation.site', 'Site')} « {fleet.name} »
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Détails de la réservation */}
        <Box sx={{ textAlign: 'left', maxWidth: 600, mx: 'auto', mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('reservation:confirmation.period', 'Période')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {t('reservation:confirmation.from', 'Du')} {formatDate(reservation.startDate)}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {t('reservation:confirmation.to', 'au')} {formatDate(reservation.endDate)}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('reservation:confirmation.purpose', 'Motif')}
            </Typography>
            <Typography variant="body1">{reservation.purpose}</Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('reservation:confirmation.email', 'Email')}
            </Typography>
            <Typography variant="body1">{reservation.userEmail}</Typography>
          </Box>

          {reservation.notes && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('reservation:confirmation.notes', 'Notes')}
              </Typography>
              <Typography variant="body1">{reservation.notes}</Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Message de confirmation */}
        <Typography paragraph color="text.secondary">
          {reservation.requiresEmailValidation && !reservation.validated
            ? t('reservation:confirmation.pendingValidation', 'Un email de confirmation vous a été envoyé.')
            : t('reservation:confirmation.message', 'Votre réservation a été enregistrée avec succès.')}
        </Typography>

        {/* Boutons d'action */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadICS}
            sx={{ textTransform: 'none' }}
          >
            {t('reservation:confirmation.downloadICS', 'Télécharger l\'ICS')}
          </Button>

          {reservation.cancellableByUser && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialogOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              {t('reservation:confirmation.cancelReservation', 'Annuler ma réservation')}
            </Button>
          )}

          {onNewReservation && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onNewReservation}
              sx={{ textTransform: 'none' }}
            >
              {t('reservation:confirmation.newReservation', 'Nouvelle réservation')}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Dialogue de confirmation d'annulation */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
      >
        <DialogTitle>
          {t('reservation:confirmation.cancelDialog.title', 'Annuler la réservation ?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'reservation:confirmation.cancelDialog.message',
              'Êtes-vous sûr de vouloir annuler cette réservation ? Cette action est irréversible.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
            {t('reservation:common.back', 'Retour')}
          </Button>
          <Button
            onClick={handleCancelReservation}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={20} /> : <CancelIcon />}
          >
            {cancelling
              ? t('reservation:common.processing', 'Traitement...')
              : t('reservation:confirmation.confirmCancel', 'Oui, annuler')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReservationConfirmation;
